import { useEffect, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import type { TweProduct, TweVariant } from 'api/generated'
import { Header } from 'components/layout/header'
import { Main } from 'components/layout/main'
import { Search } from 'components/search'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table'
import { PERIODS, type PeriodKey, periodParams } from 'helpers/twe-filters'
import {
  ArrowLeft,
  ExternalLink,
  ImageOff,
  Loader,
  MousePointerClick,
} from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store/store'
import {
  clearTweProduct,
  fetchTweProduct,
  fetchTweProductDailyClicks,
} from 'store/twe/tweSlice'
import { TweDailyClicksChart } from 'ui/components/TweDailyClicksChart'

const eur = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
})

/**
 * The feed's description is not sanitised server-side, so it never reaches the
 * DOM as markup. DOMParser reads it without running scripts or fetching assets.
 */
const asPlainText = (value: string) =>
  new DOMParser()
    .parseFromString(value, 'text/html')
    .body.textContent?.trim() ?? ''

const Gallery = ({ images, title }: { images: string[]; title: string }) => {
  const [selected, setSelected] = useState(0)

  if (images.length === 0) {
    return (
      <div className='flex aspect-square w-full items-center justify-center rounded-lg border text-muted-foreground'>
        <ImageOff className='h-8 w-8' />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-3'>
      <img
        src={images[selected]}
        alt={title}
        className='aspect-square w-full rounded-lg border bg-white object-contain'
      />
      {images.length > 1 && (
        <div className='flex flex-wrap gap-2'>
          {images.map((image, index) => (
            <button
              key={image}
              type='button'
              onClick={() => setSelected(index)}
              aria-label={`Immagine ${index + 1}`}
              aria-pressed={index === selected}
              className={`h-14 w-14 overflow-hidden rounded-md border bg-white ${
                index === selected ? 'ring-2 ring-ring' : 'opacity-70'
              }`}
            >
              <img
                src={image}
                alt=''
                className='h-full w-full object-contain'
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const VariantsTable = ({ variants }: { variants: TweVariant[] }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Taglia</TableHead>
        <TableHead>Colore</TableHead>
        <TableHead>SKU / EAN</TableHead>
        <TableHead className='text-right'>Prezzo</TableHead>
        <TableHead className='text-right'>Sconto</TableHead>
        <TableHead className='text-right'>Premium</TableHead>
        <TableHead className='text-right'>Spedizione</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {variants.map((variant) => (
        <TableRow key={variant.id}>
          <TableCell className='font-medium'>{variant.size || '—'}</TableCell>
          <TableCell>{variant.color || '—'}</TableCell>
          <TableCell className='text-xs text-muted-foreground'>
            <div>{variant.sku}</div>
            <div className='tabular-nums'>{variant.ean || '—'}</div>
          </TableCell>
          <TableCell className='text-right tabular-nums'>
            {eur.format(variant.price)}
            {variant.former_price != null && (
              <div className='text-xs text-muted-foreground line-through'>
                {eur.format(variant.former_price)}
              </div>
            )}
          </TableCell>
          <TableCell className='text-right tabular-nums'>
            {variant.discount ? `−${variant.discount}%` : '—'}
          </TableCell>
          <TableCell className='text-right text-violet-600 tabular-nums'>
            {variant.premium_price != null
              ? eur.format(variant.premium_price)
              : '—'}
            {variant.premium_discount ? (
              <div className='text-xs'>−{variant.premium_discount}%</div>
            ) : null}
          </TableCell>
          <TableCell className='text-right tabular-nums'>
            {eur.format(variant.delivery_costs)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

const DailyClicks = ({ id }: { id: number }) => {
  const dispatch = useAppDispatch()
  const { productDaily, productDailyLoading } = useAppSelector(
    (state) => state.twe
  )
  const [period, setPeriod] = useState<PeriodKey>('30')

  useEffect(() => {
    dispatch(fetchTweProductDailyClicks({ id, ...periodParams({ period }) }))
  }, [dispatch, id, period])

  const total = productDaily?.reduce((sum, day) => sum + day.clicks_count, 0)

  return (
    <div className='rounded-lg border p-4'>
      <div className='flex flex-wrap items-start justify-between gap-2'>
        <div>
          <h3 className='text-sm font-semibold'>Click per giorno</h3>
          <p className='text-xs text-muted-foreground'>
            {total != null
              ? `${total.toLocaleString('it-IT')} click nel periodo`
              : '—'}
          </p>
        </div>
        <div className='flex flex-wrap gap-1'>
          {PERIODS.map((p) => (
            <Button
              key={p.key}
              size='sm'
              variant={period === p.key ? 'secondary' : 'ghost'}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>
      <div className='mt-3'>
        {productDailyLoading && !productDaily ? (
          <Loader className='h-5 w-5 animate-spin' />
        ) : productDaily ? (
          <TweDailyClicksChart daily={productDaily} />
        ) : null}
      </div>
    </div>
  )
}

const ProductDetail = ({
  product,
  clicks,
}: {
  product: TweProduct
  clicks: number | null
}) => {
  const description = product.description
    ? asPlainText(product.description)
    : ''

  return (
    <div className='grid gap-8 lg:grid-cols-[320px_minmax(0,1fr)]'>
      <Gallery images={product.images} title={product.title} />

      <div className='flex flex-col gap-5'>
        <div>
          <p className='text-sm font-semibold tracking-wide text-muted-foreground uppercase'>
            {product.brand}
          </p>
          <h3 className='mt-1 text-2xl font-bold tracking-tight'>
            {product.title}
          </h3>
          <div className='mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
            <span className='tabular-nums'>HAN {product.han}</span>
            <span>·</span>
            <span>{product.gender}</span>
            {product.categories.map((category) => (
              <Badge key={category.id} variant='outline'>
                {category.name}
              </Badge>
            ))}
          </div>
        </div>

        <div className='flex w-fit flex-wrap items-end gap-x-6 gap-y-3 rounded-lg border p-4'>
          <div>
            <p className='text-xs text-muted-foreground'>Prezzo</p>
            <p className='text-2xl font-bold tabular-nums'>
              {product.min_price === product.max_price
                ? eur.format(product.min_price)
                : `${eur.format(product.min_price)} – ${eur.format(product.max_price)}`}
            </p>
            {product.former_price != null && (
              <p className='text-sm text-muted-foreground tabular-nums line-through'>
                {eur.format(product.former_price)}
              </p>
            )}
          </div>
          {product.premium_price != null && (
            <div>
              <p className='text-xs text-muted-foreground'>Premium</p>
              <p className='text-xl font-semibold text-violet-600 tabular-nums'>
                {eur.format(product.premium_price)}
              </p>
            </div>
          )}
          <div>
            <p className='text-xs text-muted-foreground'>Click totali</p>
            <p className='flex items-center gap-1.5 text-xl font-semibold tabular-nums'>
              <MousePointerClick className='h-4 w-4 text-muted-foreground' />
              {clicks !== null ? clicks.toLocaleString('it-IT') : '—'}
            </p>
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {product.on_sale && <Badge>Saldo</Badge>}
            {product.discountable && (
              <Badge variant='outline' className='text-violet-600'>
                Coupon premium
              </Badge>
            )}
            {product.new_item && <Badge variant='outline'>Novità</Badge>}
            {product.featured && <Badge variant='outline'>In evidenza</Badge>}
          </div>
        </div>

        <div>
          <p className='mb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
            Descrizione
          </p>
          <p className='max-w-prose text-sm whitespace-pre-line'>
            {description || (
              <span className='text-muted-foreground'>
                Il feed TWE non fornisce una descrizione per questo prodotto.
              </span>
            )}
          </p>
        </div>

        <div>
          <div className='mb-2 flex items-baseline gap-2'>
            <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
              Varianti disponibili
            </p>
            <span className='text-xs text-muted-foreground tabular-nums'>
              {product.variants.length}
            </span>
          </div>
          {product.variants.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              Nessuna variante disponibile.
            </p>
          ) : (
            <div className='overflow-x-auto rounded-md border'>
              <VariantsTable variants={product.variants} />
            </div>
          )}
        </div>

        <div>
          <Button asChild variant='outline'>
            <a href={product.url} target='_blank' rel='noreferrer'>
              Apri su Tennis Warehouse
              <ExternalLink className='h-4 w-4' />
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

const TweProductPage = () => {
  const { id } = useParams({ from: '/protected/twe/prodotti/$id' }) as {
    id: string
  }
  const dispatch = useAppDispatch()
  const { product, productClicks, productLoading, productError } =
    useAppSelector((state) => state.twe)

  useEffect(() => {
    const numericId = parseInt(id, 10)
    if (!isNaN(numericId)) dispatch(fetchTweProduct(numericId))
    return () => {
      dispatch(clearTweProduct())
    }
  }, [dispatch, id])

  return (
    <>
      {/* ===== Top Heading ===== */}
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
        </div>
      </Header>

      {/* ===== Main ===== */}
      <Main>
        <Link
          to='/twe/prodotti-piu-cliccati'
          className='inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground'
        >
          <ArrowLeft className='h-4 w-4' />
          Prodotti più cliccati
        </Link>

        <div className='-mx-4 mt-5 flex-1 overflow-auto px-4 py-1'>
          {productLoading ? (
            <Loader className='h-5 w-5 animate-spin' />
          ) : productError ? (
            <p className='text-red-500'>{productError}</p>
          ) : product ? (
            <div className='flex flex-col gap-6'>
              <DailyClicks id={product.id} />
              <ProductDetail product={product} clicks={productClicks} />
            </div>
          ) : null}
        </div>
      </Main>
    </>
  )
}

export default TweProductPage
